/**
 * FUENTE ÚNICA de color por estado / categoría de una solicitud.
 *
 * Los tres bloques del dashboard/lista que representan los MISMOS estados usan
 * esta paleta para ser consistentes:
 *   1. "Mis últimas solicitudes" (pills)          · por estado  · TONO_PILL
 *   2. Donut "Distribución · últimos 3 meses"      · por categoría · hexDeCategoria
 *   3. Columna "Estado" del listado (punto)        · por estado  · TONO_HEX
 *
 * La paleta se tomó del bloque 1 ("Mis últimas solicitudes"). Para los estados
 * que ahí caen en el mismo tono pero el donut necesita distinguir (Canceladas
 * vs Baja vs Descartadas, todos slate), se usan variantes de slate consistentes.
 */

export type Tono =
  | "emerald"
  | "teal"
  | "blue"
  | "amber"
  | "purple"
  | "indigo"
  | "red"
  | "slate";

/** Hex sólido para superficies (punto de la columna Estado). Paleta Tailwind 500. */
export const TONO_HEX: Record<Tono, string> = {
  emerald: "#10b981",
  teal: "#14b8a6",
  blue: "#3b82f6",
  amber: "#f59e0b",
  purple: "#a855f7",
  indigo: "#6366f1",
  red: "#ef4444",
  slate: "#64748b",
};

/**
 * Clases Tailwind del pill pastel (con variante dark) · las usa "Mis últimas
 * solicitudes". Clases LITERALES para que Tailwind las genere.
 */
export const TONO_PILL: Record<Tono, string> = {
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30",
  teal: "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-500/15 dark:text-teal-300 dark:border-teal-500/30",
  blue: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30",
  amber: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30",
  purple: "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-500/15 dark:text-purple-300 dark:border-purple-500/30",
  indigo: "bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/30",
  red: "bg-red-100 text-red-800 border-red-200 dark:bg-red-500/15 dark:text-red-300 dark:border-red-500/30",
  slate: "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-700/50 dark:text-slate-200 dark:border-slate-600",
};

/**
 * Tono por ESTADO (enum del backend). Es la clasificación de "Mis últimas
 * solicitudes" (bloque de referencia): verde=autorización, azul=en proceso
 * activo, índigo=autorizada, rojo=rechazos, ámbar=suspensión SAT, morado=
 * armado (prorrateo/docs), slate=captura inicial / terminales.
 */
export function tonoDeEstado(estado: string): Tono {
  switch (estado) {
    case "EnAutorizacionNivel1":
      return "teal"; // primer nivel
    case "EnAutorizacionNivelN":
      return "emerald"; // segundo nivel / Mesa de Control
    case "SeleccionandoProveedor":
    case "CapturandoDatos":
    case "Validado":
    case "EnValidacionFiscal":
    case "EnValidacionReglas":
      return "blue";
    case "ReglasRechazadas":
    case "RechazadoFiscal":
    case "RechazadaParaCorreccion":
      return "red";
    case "SuspendidaFiscal":
      return "amber";
    case "Prorrateando":
    case "ProvisionalmenteListo":
    case "AdjuntandoDocumentos":
    case "ListoParaAutorizacion":
    case "ContratoAnualizadoSeleccionado":
    case "OrdenDeCompraSeleccionada":
      return "purple";
    case "Autorizada":
    case "EnColaContabilizacion":
    case "PolizaGenerada":
    case "EnviadaAContabilizacion":
      return "indigo";
    default:
      return "slate"; // iniciación, CFDI, cancelada, baja, descartada…
  }
}

/**
 * Hex por CATEGORÍA de resultado (donut) · color REPRESENTATIVO consistente con
 * `tonoDeEstado`. Las tres categorías terminales que el bloque 1 pinta slate
 * (Cancelada/Baja/Descartada) se distinguen con variantes de slate para que el
 * donut sea legible, manteniendo la familia de color.
 */
const CATEGORIA_HEX: Record<string, string> = {
  EnProceso: TONO_HEX.blue,
  EnAutorizacion: TONO_HEX.emerald,
  Autorizada: TONO_HEX.indigo,
  Cancelada: TONO_HEX.slate,
  RechazadaPorReglas: TONO_HEX.red,
  RechazadaPorSat: TONO_HEX.amber,
  BajaMesaControl: "#334155", // slate-700 · variante más oscura para distinguir de Cancelada
  DescartadaPlazoVencido: "#94a3b8", // slate-400 · variante más clara
};

export function hexDeCategoria(categoria: string): string {
  return CATEGORIA_HEX[categoria] ?? TONO_HEX.slate;
}
